import React from "react";
import { Helmet } from "react-helmet-async";

export default function KeyholdingTerms() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">
      <Helmet>
        <title>Keyholding Terms & Conditions | SeguraTech</title>
        <meta
          name="description"
          content="Terms and conditions governing the SeguraTech Secure Keyholding and Emergency Access service."
        />
      </Helmet>

      {/* Page header */}
      <section className="max-w-6xl mx-auto px-4 pt-20 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#C6A564] mb-6">
            Keyholding Terms & Conditions
          </h1>
          <p className="text-lg text-white/80">
            Secure Keyholding & Emergency Access  
            <br />
            Version v1.0 · Last reviewed 02 January 2026  
            <br />
            Governing law: England and Wales
          </p>
        </div>
      </section>

      {/* Terms content */}
      <section className="max-w-6xl mx-auto px-4 pb-24">
        <div className="max-w-3xl mx-auto space-y-10 text-white/80">

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              1. Definitions
            </h2>
            <p>“SeguraTech” means SeguraTech Ltd.</p>
            <p>“Service” means Secure Keyholding & Emergency Access.</p>
            <p>“Contract Holder” means the individual(s) named in the Secure Keyholding Agreement.</p>
            <p>“Agreement” means the Secure Keyholding Agreement entered into between SeguraTech and the Contract Holder(s).</p>
            <p>“Call-out” means any attendance or attempted attendance by SeguraTech in response to a request for access, whether or not access is ultimately provided.</p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              2. Scope of the Service
            </h2>
            <p>
              The Service provides secure storage of keys and emergency attendance to allow
              the verified Contract Holder only to regain access to a specified property or
              vehicle following a lock-out.
            </p>
            <p>The Service is intended to prevent the need for a locksmith and to provide reassurance in emergency situations.</p>
            <p>The Service does not include caretaking, welfare checks, routine access, third-party access, vehicle movement, or locksmith services beyond providing access.</p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              3. Eligibility and Authority
            </h2>
            <p>
              Access will be provided only to the verified Contract Holder. There are no exceptions.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              4. Identity Verification
            </h2>
            <p>
              Identity verification is mandatory. If identity cannot be verified to
              SeguraTech’s satisfaction, access will be refused.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              5. Recording
            </h2>
            <p>
              Access-related calls may be recorded and body-worn cameras are used during attendances.
              Recording is always declared. If recording is refused, the Service cannot be provided.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              6. Call-Out Allowance and Charges
            </h2>
            <p>
              The Service includes a limited number of call-outs per month. Additional attendances
              are chargeable and deemed to occur once travel begins, regardless of access outcome.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              7. Payment
            </h2>
            <p>
              Where additional charges apply, payment is due within 24 hours. Failure to settle
              payment may result in suspension or cancellation.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              8. Keys
            </h2>
            <p>
              Keys remain in SeguraTech’s custody at all times, are securely stored, tracked,
              never labelled with addresses, and never handed over.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              9. Customer Obligations
            </h2>
            <p>
              Contract Holders must keep contact details accurate and notify SeguraTech of
              any changes affecting access or verification.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              10. Disputes, Separation, and Restrictions
            </h2>
            <p>
              SeguraTech does not adjudicate disputes and may suspend the Service until
              matters are clarified.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              11. Death of the Contract Holder
            </h2>
            <p>
              Keys will be released only to a solicitor acting for the estate. The Service
              terminates immediately upon death.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              12. Police and Legal Access
            </h2>
            <p>
              Access is provided only where legally required. All such requests are logged.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              13. Behaviour and Safety
            </h2>
            <p>
              Aggressive or abusive behaviour will result in immediate termination of the Service.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              14. Cancellation
            </h2>
            <p>
              Monthly plans require one month’s notice. Annual plans are non-refundable
              except within the statutory cooling-off period.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              15. Liability and Insurance
            </h2>
            <p>
              SeguraTech holds Public Liability and Professional Indemnity Insurance.
              Liability is not limited where prohibited by law.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              16. Data Protection
            </h2>
            <p>
              Personal data is processed in accordance with UK data protection law.
              Further details are available in the Privacy Notice.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              17. Changes to These Terms
            </h2>
            <p>
              These Terms may be updated from time to time. The latest version will always
              be available on the SeguraTech website.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              18. Governing Law and Jurisdiction
            </h2>
            <p>
              These Terms are governed by the laws of England and Wales.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
